import { scrollToTopOfScrollParent } from '@ardoq/dom-utils';

const SCROLL_TO_HASH_MAX_WAIT_MS = 8000;
const SCROLL_TO_HASH_SETTLE_MS = 250;
const SCROLL_TO_HASH_CHECK_INTERVAL_MS = 100;
const USER_INTERACTION_EVENTS = [
  'wheel',
  'touchmove',
  'keydown',
  'pointerdown',
] as const;

let cancelPendingScrollToHash: VoidFunction | null = null;

const decodeHashId = (hash: string): string => {
  const id = hash.replace(/^#/, '');
  try {
    return decodeURIComponent(id);
  } catch {
    return id;
  }
};

/**
 * Scroll to the element matching the given URL hash fragment.
 *
 * Polls across animation frames (re-anchoring at most once per
 * {@link SCROLL_TO_HASH_CHECK_INTERVAL_MS}) because the target element is often
 * mounted asynchronously after navigation (route content and its data load
 * after the URL changes). Keeps re-anchoring until the element's position stays
 * stable for {@link SCROLL_TO_HASH_SETTLE_MS}, so content rendering in above the
 * target doesn't leave it scrolled out of view. Gives up after
 * {@link SCROLL_TO_HASH_MAX_WAIT_MS}, when the user scrolls or interacts, or
 * when a newer scroll request supersedes it.
 */
export const scrollToHashElement = (
  hash: string | undefined,
  options?: Pick<ScrollToOptions, 'behavior'>
): void => {
  cancelPendingScrollToHash?.();

  const id = hash ? decodeHashId(hash) : '';
  if (!id) {
    return;
  }

  const startTime = performance.now();
  let lastCheck: number | null = null;
  let lastTop: number | null = null;
  let stableSince: number | null = null;
  let frame: number;

  const stop = () => {
    cancelAnimationFrame(frame);
    for (const event of USER_INTERACTION_EVENTS) {
      window.removeEventListener(event, stop, { capture: true });
    }
    cancelPendingScrollToHash = null;
  };

  const tick = () => {
    const now = performance.now();
    if (now - startTime > SCROLL_TO_HASH_MAX_WAIT_MS) {
      stop();
      return;
    }

    if (
      lastCheck !== null &&
      now - lastCheck < SCROLL_TO_HASH_CHECK_INTERVAL_MS
    ) {
      frame = requestAnimationFrame(tick);
      return;
    }
    lastCheck = now;

    const element = document.getElementById(id);
    if (!element) {
      frame = requestAnimationFrame(tick);
      return;
    }

    const top = element.getBoundingClientRect().top;
    scrollToTopOfScrollParent(element, options?.behavior);

    if (lastTop !== null && Math.abs(top - lastTop) < 1) {
      stableSince ??= now;
      if (now - stableSince > SCROLL_TO_HASH_SETTLE_MS) {
        stop();
        return;
      }
    } else {
      stableSince = null;
    }
    lastTop = top;

    frame = requestAnimationFrame(tick);
  };

  cancelPendingScrollToHash = stop;
  for (const event of USER_INTERACTION_EVENTS) {
    window.addEventListener(event, stop, { passive: true, capture: true });
  }
  frame = requestAnimationFrame(tick);
};
